import { executeNextLoop, invariant } from '@ifixit/helpers';
import { LocaleCode } from '@ifixit/i18n/types';
import {
   type CountryCode,
   type LanguageCode,
   ShopifyStorefrontProvider,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import {
   CartDrawerProvider,
   CartDrawerTrigger,
   loadCartDrawerContent,
   useCartDrawer,
} from '@ifixit/cart';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import type { ElementType } from 'react';
import { MountOnFirstOpen, prefetch } from 'Shared/mount_on_first_open';
import { SmartLink } from 'Shared/smart_link';
import { SupportLink } from 'Shared/support-link';

type CartDrawerWrapperProps = {
   locale: LocaleCode | null;
   storeCode: string;
   linkComponent?: ElementType;
};

// The drawer and its contents are separate chunks, and the contents only
// render once the drawer is open, so both start together here instead of one
// after the other.
async function loadSheet() {
   const [sheet] = await Promise.all([import('./cart_drawer_sheet'), loadCartDrawerContent()]);
   return sheet;
}

/**
 * The shared CartDrawer, split so the drawer's code is fetched the first time
 * it is needed: the cart button renders with the header, its code is fetched
 * when someone points at or focuses the button, and the drawer mounts on the
 * first open from any source (the button, or Shared/cart_drawer.js clicking
 * it after an add to cart) and stays mounted after.
 */
export function CartDrawerWrapper({ locale, storeCode, linkComponent }: CartDrawerWrapperProps) {
   const { cartUrl } = useShopifyStorefrontClient();
   invariant(cartUrl, 'cart url must be provided for carpenter cart drawer');
   invariant(locale, 'locale must be provided for carpenter cart drawer');
   const { isOpen, onOpen, onClose } = useCartDrawer();
   const cart = useCart();

   return (
      <>
         {/* display: contents, so the header's flex row still sees the
             trigger's own box. */}
         <div
            className="contents"
            onPointerEnter={() => prefetch(loadSheet)}
            onFocus={() => prefetch(loadSheet)}
         >
            <CartDrawerTrigger
               onClick={() => executeNextLoop(onOpen)}
               hasItemsInCart={cart.data?.hasItemsInCart}
            />
         </div>
         <MountOnFirstOpen
            isOpen={isOpen}
            load={loadSheet}
            props={{
               localeCode: locale,
               ImageComponent: CartImage,
               supportLinkComponent: SupportLink,
               viewCartUrl: cartUrl,
               storeCode,
               linkComponent,
               recordMerchExperiment: () => true,
            }}
            onLoadFailed={onClose}
         />
      </>
   );
}

function CartImage({ alt, src }: { alt?: string; src: string }) {
   const srcUrl = new URL(src);
   const params = new URLSearchParams(srcUrl.search);
   params.set('width', '70');
   const newSrc = `${srcUrl.origin}${srcUrl.pathname}?${params.toString()}`;
   return <img src={newSrc} alt={alt ?? ''} className="object-cover" />;
}

type MobileAppCartDrawerProps = {
   shopifyStore: {
      country: CountryCode;
      currencyCode: string;
      language: LanguageCode;
      storeCode: string;
      shopDomain: string;
      storefrontAccessToken: string;
      cartUrl: string;
      storeUrl: string;
   };
   cartDrawer: {
      locale: LocaleCode | null;
   };
};

/**
 * The cart drawer without the rest of the header, mounted by
 * MobileAppCartDrawerComponent on native-app requests. The apps hide the
 * header chrome, but the guide "Add to cart" buttons open the drawer by
 * clicking its trigger (Shared/cart_drawer.js), so the trigger has to be in
 * the DOM. It is hidden: a dispatched click still reaches a display:none
 * element, and the drawer itself renders in a portal.
 */
export function MobileAppCartDrawer({ shopifyStore, cartDrawer }: MobileAppCartDrawerProps) {
   return (
      <ShopifyStorefrontProvider {...shopifyStore}>
         <CartDrawerProvider>
            <div hidden>
               <CartDrawerWrapper
                  locale={cartDrawer.locale}
                  storeCode={shopifyStore.storeCode}
                  linkComponent={SmartLink}
               />
            </div>
         </CartDrawerProvider>
      </ShopifyStorefrontProvider>
   );
}
